import './cta-sticky.scss';
import { gsap } from 'gsap';

const OPEN_DURATION  = 0.55;
const CLOSE_DURATION = 0.45;
const EASE_OPEN      = 'power3.out';
const EASE_CLOSE     = 'power3.inOut';

/**
 * Convert a computed CSS length (px, rem, em) to pixels.
 * parseFloat( '17.5rem' ) would yield a tiny wrong value for GSAP width.
 *
 * @param {string} value Raw value from getPropertyValue().
 * @return {number} Pixels, or NaN if unsupported.
 */
function cssLengthToPx( value ) {
	const raw = String( value || '' ).trim();
	if ( '' === raw || 'auto' === raw ) {
		return NaN;
	}

	const m = raw.match( /^(-?[\d.]+)([a-z%]*)$/i );
	if ( ! m ) {
		return NaN;
	}

	const num = parseFloat( m[1] );
	const unit = ( m[2] || 'px' ).toLowerCase();

	if ( 'px' === unit || '' === unit ) {
		return num;
	}

	const rootFontPx = parseFloat( getComputedStyle( document.documentElement ).fontSize ) || 16;

	if ( 'rem' === unit || 'em' === unit ) {
		return num * rootFontPx;
	}

	return NaN;
}

function initCtaSticky( root ) {
	const inner   = root.querySelector( '.p-ctaSticky__inner' );
	const card    = root.querySelector( '.p-ctaSticky__card' );
	const close   = root.querySelector( '[data-cta-sticky-close]' );
	const toggle  = root.querySelector( '[data-cta-sticky-toggle]' );
	const actions = root.querySelector( '.p-ctaSticky__actions' );

	const titleClosed    = root.querySelector( '.p-ctaSticky__title--closed' );
	const titleOpen      = root.querySelector( '.p-ctaSticky__title--open' );
	const subtitleClosed = root.querySelector( '.p-ctaSticky__subtitle--closed' );
	const subtitleOpen   = root.querySelector( '.p-ctaSticky__subtitle--open' );

	if ( ! inner || ! card || ! toggle ) {
		return;
	}

	const buttons = actions ? Array.from( actions.querySelectorAll( '.p-ctaSticky__button' ) ) : [];

	const styles    = getComputedStyle( root );
	const isMobile  = window.matchMedia( '(max-width: 767px)' ).matches;
	const closedVar = styles.getPropertyValue( '--cta-sticky-closed-width' ).trim();
	const openVar   = styles.getPropertyValue( '--cta-sticky-open-width' ).trim();
	const closedPxFromVar = cssLengthToPx( closedVar );
	const openPxFromVar   = cssLengthToPx( openVar );

	// Desktop: prefer live layout width (handles rem + max-width); fallback to parsed var or design px.
	const closedWidthPx = isMobile
		? 281
		: ( inner.offsetWidth || closedPxFromVar || 281 );
	const openWidthPx = openPxFromVar || 536;

	if ( isMobile ) {
		gsap.set( inner, { width: '100%' } );
	} else {
		gsap.set( inner, { width: closedWidthPx } );
	}
	if ( actions ) {
		gsap.set( actions, { height: 0, opacity: 0, marginTop: 0 } );
	}

	if ( titleOpen ) {
		gsap.set( titleOpen, { opacity: 0, y: 6 } );
	}
	if ( subtitleOpen ) {
		gsap.set( subtitleOpen, { opacity: 0, y: 6 } );
	}
	if ( titleClosed ) {
		gsap.set( titleClosed, { opacity: 1, y: 0 } );
	}
	if ( subtitleClosed ) {
		gsap.set( subtitleClosed, { opacity: 1, y: 0 } );
	}
	// Opacity stays on the actions wrapper only; hiding is done via height + overflow.
	if ( buttons.length ) {
		gsap.set( buttons, { y: 12 } );
	}

	let isOpen   = false;
	let activeTl = null;

	/**
	 * Full height + padding as applied when open (for GSAP sync with CSS).
	 *
	 * @return {{ height: number, paddingTop: number, paddingRight: number, paddingBottom: number, paddingLeft: number }}
	 */
	const measureActions = () => {
		const empty = {
			height: 0,
			paddingTop: 0,
			paddingRight: 0,
			paddingBottom: 0,
			paddingLeft: 0,
		};

		if ( ! actions ) {
			return empty;
		}

		const prev = {
			height:   actions.style.height,
			opacity:  actions.style.opacity,
			position: actions.style.position,
			visibility: actions.style.visibility,
		};

		actions.style.height     = 'auto';
		actions.style.opacity    = '0';
		actions.style.position   = 'absolute';
		actions.style.visibility = 'hidden';

		const cs = getComputedStyle( actions );
		const paddingTop    = parseFloat( cs.paddingTop ) || 0;
		const paddingRight  = parseFloat( cs.paddingRight ) || 0;
		const paddingBottom = parseFloat( cs.paddingBottom ) || 0;
		const paddingLeft   = parseFloat( cs.paddingLeft ) || 0;
		const h             = actions.offsetHeight;

		actions.style.height     = prev.height;
		actions.style.opacity    = prev.opacity;
		actions.style.position   = prev.position;
		actions.style.visibility = prev.visibility;

		return {
			height: h,
			paddingTop,
			paddingRight,
			paddingBottom,
			paddingLeft,
		};
	};

	const setExpanded = ( open ) => {
		toggle.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
		root.setAttribute( 'data-state', open ? 'open' : 'closed' );
		buttons.forEach( ( btn ) => {
			btn.setAttribute( 'tabindex', open ? '0' : '-1' );
		} );
	};

	const openCta = () => {
		if ( isOpen ) {
			return;
		}

		isOpen = true;
		setExpanded( true );

		if ( activeTl ) {
			activeTl.kill();
		}

		if ( actions ) {
			actions.style.removeProperty( 'padding' );
			actions.style.removeProperty( 'padding-top' );
			actions.style.removeProperty( 'padding-right' );
			actions.style.removeProperty( 'padding-bottom' );
			actions.style.removeProperty( 'padding-left' );
		}

		const measured = measureActions();
		const targetWidth   = window.matchMedia( '(max-width: 767px)' ).matches ? inner.offsetWidth : openWidthPx;

		activeTl = gsap.timeline( { defaults: { ease: EASE_OPEN } } );
		activeTl.to( inner, { width: targetWidth, duration: OPEN_DURATION }, 0 );

		const addIfTarget = ( target, vars, position ) => {
			if ( target ) {
				activeTl.to( target, vars, position );
			}
		};

		addIfTarget( titleClosed, { opacity: 0, y: -6, duration: 0.25 }, 0 );
		addIfTarget( subtitleClosed, { opacity: 0, y: -6, duration: 0.25 }, 0 );
		addIfTarget( titleOpen, { opacity: 1, y: 0, duration: 0.35 }, 0.15 );
		addIfTarget( subtitleOpen, { opacity: 1, y: 0, duration: 0.35 }, 0.2 );

		if ( actions && measured.height > 0 ) {
			activeTl.fromTo(
				actions,
				{
					height: 0,
					opacity: 0,
					marginTop: 0,
					paddingTop: 0,
					paddingRight: 0,
					paddingBottom: 0,
					paddingLeft: 0,
				},
				{
					height: measured.height,
					opacity: 1,
					marginTop: 16,
					paddingTop: measured.paddingTop,
					paddingRight: measured.paddingRight,
					paddingBottom: measured.paddingBottom,
					paddingLeft: measured.paddingLeft,
					duration: 0.4,
				},
				0.15
			);
			if ( buttons.length ) {
				activeTl.to( buttons, { y: 0, stagger: 0.06, duration: 0.35 }, 0.25 );
			}
		}
	};

	const closeCta = () => {
		if ( ! isOpen ) {
			return;
		}

		isOpen = false;

		if ( activeTl ) {
			activeTl.kill();
		}

		/** @type {{ paddingTop: number, paddingRight: number, paddingBottom: number, paddingLeft: number }|null} */
		let openPadding = null;

		if ( actions ) {
			const cs = getComputedStyle( actions );
			openPadding = {
				paddingTop: parseFloat( cs.paddingTop ) || 0,
				paddingRight: parseFloat( cs.paddingRight ) || 0,
				paddingBottom: parseFloat( cs.paddingBottom ) || 0,
				paddingLeft: parseFloat( cs.paddingLeft ) || 0,
			};
		}

		setExpanded( false );

		if ( actions && openPadding ) {
			gsap.set( actions, openPadding );
		}

		const targetWidth = window.matchMedia( '(max-width: 767px)' ).matches ? inner.offsetWidth : closedWidthPx;

		activeTl = gsap.timeline( { defaults: { ease: EASE_CLOSE } } );

		const addIfTargetClose = ( target, vars, position ) => {
			if ( target ) {
				activeTl.to( target, vars, position );
			}
		};

		if ( actions && buttons.length ) {
			activeTl.to( buttons, { y: 8, stagger: -0.04, duration: 0.2 }, 0 );
		}

		if ( actions ) {
			activeTl.to(
				actions,
				{
					height: 0,
					marginTop: 0,
					opacity: 0,
					paddingTop: 0,
					paddingRight: 0,
					paddingBottom: 0,
					paddingLeft: 0,
					duration: 0.35,
				},
				0.05
			);
		}

		addIfTargetClose( titleOpen, { opacity: 0, y: 6, duration: 0.2 }, 0 );
		addIfTargetClose( subtitleOpen, { opacity: 0, y: 6, duration: 0.2 }, 0 );
		addIfTargetClose( titleClosed, { opacity: 1, y: 0, duration: 0.3 }, 0.1 );
		addIfTargetClose( subtitleClosed, { opacity: 1, y: 0, duration: 0.3 }, 0.15 );
		activeTl.to( inner, { width: targetWidth, duration: CLOSE_DURATION }, 0.05 );
	};

	toggle.addEventListener( 'click', ( event ) => {
		if ( isOpen ) {
			return;
		}

		event.preventDefault();
		openCta();
	} );

	if ( close ) {
		close.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			event.stopPropagation();
			closeCta();
		} );
	}

	document.addEventListener( 'keydown', ( event ) => {
		if ( 'Escape' === event.key && isOpen ) {
			closeCta();
		}
	} );
}

function initAll() {
	document.querySelectorAll( '[data-component="cta-sticky"]' ).forEach( ( el ) => {
		if ( el.dataset.ctaStickyReady === '1' ) {
			return;
		}

		el.dataset.ctaStickyReady = '1';
		initCtaSticky( el );
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initAll );
} else {
	initAll();
}
